// /v/:vaultId and /v/:vaultId/*path — the vault view, rendered INSIDE the app
// shell (decisions/90). The path shows as a breadcrumb in the shell header;
// Members + History are header actions (they open Sheets); the file tree lives
// in a resizable left panel of the content area with the file content to its
// right. Mints a sync token, opens the reference lazy-subscription client, and
// renders live, read-only content.

import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  IconAlertTriangle,
  IconClockHour4,
  IconLoader2,
  IconUsers,
  IconWifi,
  IconWifiOff,
} from "@tabler/icons-react";
import type { Icon } from "@tabler/icons-react";
import { META_DOC_ID, type FileEntry, type SyncStatus } from "@cairn/core";
import { listVaults, mintSyncToken } from "../api";
import { makeNet } from "../net";
import { MetaSyncError, ViewerSync } from "../viewerSync";
import { useApi, useUser } from "../session";
import { useAsyncData } from "../lib/use-async";
import { useDelayedLoading } from "../lib/use-delayed-loading";
import { useShellHeader } from "../app/shell-header";
import { FileTree } from "../components/FileTree";
import { FileView } from "../components/FileView";
import { PresenceDots } from "../components/PresenceDots";
import { HistoryView } from "../components/HistoryView";
import { CairnMark } from "@/components/cairn-mark";
import { CloneVaultButton } from "@/components/clone-vault-button";
import { VaultBreadcrumbs } from "@/components/vault-breadcrumbs";
import { VaultMembersSheet } from "@/components/vault-members-sheet";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { cn } from "@/lib/utils";

const STATUS_LABEL: Record<SyncStatus, string> = {
  offline: "offline",
  connecting: "connecting",
  live: "live",
  error: "error",
};

// A status glyph (not a bare dot) so the pill's leading mark carries the same
// visual weight as the label — a 6px dot left the strip looking padded-heavy on
// the left. Icon color still carries the state at a glance, as the dot did.
const STATUS_ICON: Record<SyncStatus, { Icon: Icon; className: string }> = {
  offline: { Icon: IconWifiOff, className: "text-muted-foreground" },
  connecting: { Icon: IconLoader2, className: "animate-spin text-amber-500" },
  live: { Icon: IconWifi, className: "text-emerald-500" },
  error: { Icon: IconAlertTriangle, className: "text-destructive" },
};

function SyncStatusPill({ status }: { status: SyncStatus }) {
  const { Icon, className } = STATUS_ICON[status];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 text-xs text-muted-foreground",
        status === "error" && "text-destructive",
      )}
      title={`Sync: ${STATUS_LABEL[status]}`}
    >
      <Icon className={cn("size-4 shrink-0", className)} aria-hidden="true" />
      <span className="font-mono">{STATUS_LABEL[status]}</span>
    </span>
  );
}

/** Right-aligned header actions: presence (open note) + Clone + Members +
 *  History. */
function VaultHeaderActions({
  sync,
  vaultId,
  fileId,
  historyActive,
  onMembers,
  onHistory,
}: {
  sync: ViewerSync | null;
  vaultId: string;
  fileId: string | undefined;
  historyActive: boolean;
  onMembers: () => void;
  onHistory: () => void;
}) {
  return (
    <div className="flex items-center gap-1.5">
      {sync && fileId && <PresenceDots sync={sync} docId={fileId} />}
      <CloneVaultButton vaultId={vaultId} />
      <Button variant="ghost" size="sm" onClick={onMembers}>
        <IconUsers data-icon="inline-start" />
        Members
      </Button>
      {sync && fileId && (
        <Button
          variant={historyActive ? "secondary" : "ghost"}
          size="sm"
          aria-pressed={historyActive}
          onClick={onHistory}
        >
          <IconClockHour4 data-icon="inline-start" />
          History
        </Button>
      )}
    </div>
  );
}

/** Turn an open/meta-sync failure into a human sentence with a clear cause. */
function describeOpenError(e: unknown): string {
  if (e instanceof MetaSyncError) {
    return `This vault couldn't be opened: ${e.message}.`;
  }
  const msg = e instanceof Error ? e.message : String(e);
  // The bare "doc meta did not sync within Nms" timeout is meaningless to a
  // user — most often it's a freshly-created vault whose storage isn't ready,
  // or a dropped connection. Say so, and let them retry.
  if (msg.includes("did not sync")) {
    return (
      "This vault couldn't be opened — its index didn't load in time. " +
      "If it was just created its storage may not be ready yet; otherwise the " +
      "connection may have dropped."
    );
  }
  return msg;
}

export function VaultPage() {
  const api = useApi();
  const user = useUser();
  const params = useParams();
  const vaultId = params.vaultId ?? "";
  const path = params["*"] ? decodeURIComponent(params["*"]) : "";

  const [sync, setSync] = useState<ViewerSync | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<SyncStatus>("connecting");
  const [membersOpen, setMembersOpen] = useState(false);
  const [historyMode, setHistoryMode] = useState(false);
  const [canRestore, setCanRestore] = useState(false);
  // Bumped by "Try again" to re-run the open effect after a failure.
  const [retry, setRetry] = useState(0);

  // History is per-file: leaving the file (or opening a non-note) exits it.
  useEffect(() => {
    setHistoryMode(false);
  }, [path]);

  // Vault name for the breadcrumb (from the caller's memberships).
  const vaults = useAsyncData(() => listVaults(api), []);
  const vaultName = vaults.data?.find((v) => v.vault.id === vaultId)?.vault.name ?? vaultId;

  useEffect(() => {
    let cancelled = false;
    let created: ViewerSync | null = null;
    setSync(null);
    setError(null);
    (async () => {
      const grant = await mintSyncToken(api, vaultId);
      // First connect reuses the token just minted; refreshes re-mint.
      let firstToken: string | null = grant.sync_token;
      const mint = async (): Promise<string> => {
        if (firstToken !== null) {
          const t = firstToken;
          firstToken = null;
          return t;
        }
        return (await mintSyncToken(api, vaultId)).sync_token;
      };
      created = new ViewerSync(makeNet(api), {
        vaultId,
        wsUrl: grant.ws_url,
        mintToken: mint,
        displayName: user.display_name,
      });
      created.statusChanged.on((s) => {
        if (!cancelled) setStatus(s);
      });
      await created.start();
      if (cancelled) created.stop();
      else setSync(created);
    })().catch((e: unknown) => {
      if (!cancelled) setError(describeOpenError(e));
    });
    return () => {
      cancelled = true;
      created?.stop();
    };
  }, [api, vaultId, user.display_name, retry]);

  // Live meta entries (re-read on every meta change).
  const [metaGen, setMetaGen] = useState(0);
  useEffect(() => {
    if (!sync) return;
    return sync.onDocChange(META_DOC_ID, () => setMetaGen((g) => g + 1));
  }, [sync]);
  const entries: FileEntry[] = useMemo(
    () => sync?.meta()?.active() ?? [],
    // metaGen invalidates the memo when the meta doc changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sync, metaGen],
  );

  // Delay every "connecting" visual so a quick connect never flashes them:
  // `showConnecting` gates the initial no-sync placeholders (tree skeleton +
  // the content-pane message); `showConnectingStatus` gates the status pill,
  // which tracks the live `status` (so it also covers a mid-session reconnect).
  const showConnecting = useDelayedLoading(!sync);
  const showConnectingStatus = useDelayedLoading(status === "connecting");

  const currentEntry = entries.find((e) => e.path === path);
  const isNote = currentEntry?.kind === "md";
  const noteId = isNote ? currentEntry?.fileId : undefined;

  // Restore is editor+ (history spec); track the live role.
  useEffect(() => {
    if (!sync) return;
    setCanRestore(sync.canRestore());
    return sync.roleChanged.on(() => setCanRestore(sync.canRestore()));
  }, [sync]);

  // Feed the shell header: breadcrumb + Members/History/presence actions.
  useShellHeader(
    <VaultBreadcrumbs vaultId={vaultId} vaultName={vaultName} path={path} />,
    <VaultHeaderActions
      sync={sync}
      vaultId={vaultId}
      fileId={noteId}
      historyActive={historyMode}
      onMembers={() => setMembersOpen(true)}
      onHistory={() => setHistoryMode((m) => !m)}
    />,
    [vaultId, vaultName, path, noteId, sync !== null, historyMode],
  );

  if (error) {
    return (
      <div className="grid flex-1 place-items-center p-6">
        <div className="w-full max-w-md">
          <Empty className="border">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <CairnMark className="size-5" />
              </EmptyMedia>
              <EmptyTitle>Can't open this vault</EmptyTitle>
              <EmptyDescription>{error}</EmptyDescription>
            </EmptyHeader>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => {
                  setError(null);
                  setStatus("connecting");
                  setRetry((r) => r + 1);
                }}
              >
                Try again
              </Button>
              <Button variant="outline" render={<Link to="/" />}>
                Back to your vaults
              </Button>
            </div>
          </Empty>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100svh-4.5rem)]">
      <ResizablePanelGroup orientation="horizontal" className="h-full">
        <ResizablePanel defaultSize="24" minSize="15" maxSize="40" className="flex flex-col">
          {/* A future search bar lives above the tree (deferred — decisions/90).
              Nothing is scaffolded here yet. */}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-2">
            {sync ? (
              <FileTree vaultId={vaultId} entries={entries} currentPath={path} />
            ) : showConnecting ? (
              <div className="flex flex-col gap-2 p-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            ) : null}
          </div>
          {/* Mirror the header's box exactly (h-14 px-4) so this strip reads as
              its bottom counterpart. The header only has px-4 horizontally, but
              its sidebar-toggle button forces the bar to h-14 (56px); around
              ~20px text that's (56−20)/2 = 18px of vertical breathing room. A
              thinner strip here (min-h-9 → ~8px) looked under-padded next to it,
              so match the height, not just the horizontal token. */}
          <div className="flex h-14 items-center border-t px-4">
            {/* Suppress the "connecting" pill until the delay elapses so a fast
                connect never flashes it; other states show immediately. */}
            {(status !== "connecting" || showConnectingStatus) && (
              <span className="duration-200 animate-in fade-in">
                <SyncStatusPill status={status} />
              </span>
            )}
          </div>
        </ResizablePanel>

        <ResizableHandle withHandle />

        <ResizablePanel defaultSize="76" className="flex min-h-0 flex-col">
          {sync && historyMode && isNote && currentEntry ? (
            <HistoryView
              history={sync.history}
              fileId={currentEntry.fileId}
              canRestore={canRestore}
              vaultId={vaultId}
              entries={entries}
              api={api}
              onExit={() => setHistoryMode(false)}
            />
          ) : (
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-6 lg:p-8">
              {!sync && showConnecting && (
                <div className="flex items-center gap-2 p-4 text-muted-foreground duration-200 animate-in fade-in">
                  <Spinner className="size-4" />
                  <span className="font-mono text-xs tracking-wider uppercase">
                    Connecting to vault…
                  </span>
                </div>
              )}
              {sync && path === "" && (
                <Empty className="border">
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <CairnMark className="size-5" />
                    </EmptyMedia>
                    <EmptyTitle>Pick a file to read</EmptyTitle>
                    <EmptyDescription>
                      Choose a note or attachment from the tree. Everything here is live and
                      read-only.
                    </EmptyDescription>
                  </EmptyHeader>
                </Empty>
              )}
              {sync && path !== "" && (
                <FileView
                  key={`${vaultId}:${path}`}
                  sync={sync}
                  vaultId={vaultId}
                  path={path}
                  entries={entries}
                />
              )}
            </div>
          )}
        </ResizablePanel>
      </ResizablePanelGroup>

      <VaultMembersSheet
        vaultId={vaultId}
        currentUserId={user.id}
        open={membersOpen}
        onOpenChange={setMembersOpen}
      />
    </div>
  );
}
